import VerifiedUserOutlinedIcon from '@material-ui/icons/VerifiedUserOutlined'
import * as Common from '@tfo/common'
import { Elements, Loader, TextButton } from '@tfo/common'
import { BankId, LoginType } from '@tfo/ppl'
import { observer } from 'mobx-react'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, Redirect, useLocation } from 'react-router-dom'
import TechnicalIssuesMessage from '../../components/technical-issues-message/TechnicalIssuesMessage'
import { useStore } from '../../hooks/useStore'
import bank from '../../media/images/BankID_logo.svg'
import $http from '../../utils/$http'
import { PasswordPolicySwitch } from '../client/PasswordPolicySwitch/PasswordPolicySwitch'
import ActivateBankIdInstructions from './ActivateBankIdInstructions'
import './LoginClient.scss'
import Password from './Password'

const { Heading3, Paragraph } = Elements

const LoginClient = observer(() => {
	const intl = useIntl()
	const bankIdIcon = () => <img className="bankId-icon" src={bank} alt="BankId Logo"></img>
	const { loginStore: store } = useStore()
	const { state } = useLocation<{ returnUrl: string }>()

	const hrefQuery = new URLSearchParams(document.location.search)
	const [loginType, setLoginType] = useState<LoginType>(LoginType.None)
	const hideCancelButton: boolean = hrefQuery.get("loginWith") === "Password" ? true : false

	const login = async (encryptedCompletionData: string) => {
		store.onBankIdLogin(encryptedCompletionData)
	}
	
	const onSetLoginType = (loginType: LoginType) => {
		setLoginType(loginType)
	}

	const qrCodeBankId = async (protectedQrState: string): Promise<{ qrCode: string }> => {
		return await $http.post<{ qrCode: string }>('/authenticate/bankid/qrcode', {
			protectedQrState: protectedQrState
		})
	}

	const authBankId = async (onSameDevice: boolean): Promise<{
		protectedOrderRef: string
		protectedQrStartState: string
		qrCode: string
		launchUrl: string
	}> => {
		return await $http.post<{
			protectedOrderRef: string
			protectedQrStartState: string
			qrCode: string
			launchUrl: string
		}>('/authenticate/bankid/auth', {
			isSameDevice: onSameDevice
		})
	}

	const collectBankId = async (protectedOrderRef, attempt): Promise<{
		completionData?: string
		statusMessage?: string
		hintCode?: string
	}> => {
		return await $http.post<{
			completionData?: string
			statusMessage?: string
			hintCode?: string
		}>
			('/authenticate/bankid/collect', {
				protectedOrderRef,
				attempt
			});
	}

	const cancelBankIdSession = async (orderRef: string) => {
		await $http.post<{
			protectedOrderRef: string
			protectedQrStartState: string
			qrCode: string
		}>('/authenticate/bankid/cancel', {
			protectedOrderRef: orderRef
		})
	}

	const buildSsoLoginUrl = () => {
		const apiBaseUrl = `https://${(window as any).tfoEnv}`
		const returnUrl = `${window.location.origin}/sso`
		const ssoUrl = `${apiBaseUrl}/authenticate/sso/auth?returnUrl=${encodeURIComponent(returnUrl)}`
		
		return ssoUrl
	}

	return Common.utils.loggedIn() ? (
		Common.utils.needToChangePassword() ? (
			<PasswordPolicySwitch />
		) : (
			<Redirect to={state?.returnUrl || '/start'} />
		)
	) : (
		<div className="Container-LoginClient">
			<div className="LoginClient">
				<Heading3 className="login-header">
					{intl.formatMessage({ id: 'loginpage.login' })}
				</Heading3>
				<TechnicalIssuesMessage />
				<>
					{loginType === LoginType.None && !store.hasNoActivatedBankId &&
						<>
							<TextButton
								onClick={() => setLoginType(LoginType.BankId)}
								className="auth-button"
								secondary
								icon={bankIdIcon()}>{intl.formatMessage({ id: 'bankid.startButton' })}
							</TextButton>

							<TextButton
								onClick={() => setLoginType(LoginType.Password)}
								className={`${"auth-button"} ${"auth-button-lower"}`}
								icon={<VerifiedUserOutlinedIcon className='verfication-code-icon' />}
								secondary>{intl.formatMessage({ id: 'common.password' })}
							</TextButton>
						</>
					}
					{store.hasNoActivatedBankId ? (
						<ActivateBankIdInstructions setLoginType={setLoginType} />

					) : (loginType === LoginType.BankId &&
						<BankId
							authenticateEncryptedData={login}
							startAuthOnDisplay={false}
							cancelBankIdSession={cancelBankIdSession}
							collectBankId={collectBankId}
							authBankId={authBankId}
							onReset={onSetLoginType}
							qrCodeBankId={qrCodeBankId}
						/>
					)}

				</>
				{loginType === LoginType.Password &&
					<>
						<Password
							onReset={onSetLoginType}
							hideCancelButton={hideCancelButton} />
					</>
				}
				{loginType === LoginType.None &&
					<div className='paragraph-container'>
						<Paragraph className="no-password">
							<Link to="/forgot-create-password" className="forgot-password-link">
								<FormattedMessage id="preamble.no-password" />
							</Link>
						</Paragraph>
						<Paragraph className='sso-link'>
							<a href={buildSsoLoginUrl()} className="sso-link">
								<FormattedMessage id="common.loginWithSSO" />
							</a>
						</Paragraph>
					</div>}

				{store.loading && <Loader />}
			</div>
		</div>
	)
})

export default LoginClient
